import React, { useEffect, useMemo, useState } from 'react';
import CampoNumero from './CampoNumero';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import { obtenerClientes } from '../services/clientesService';
import { obtenerServicios, obtenerAdicionalesPredef } from '../services/serviciosService';
import { obtenerProductos } from '../services/productosService';
import { obtenerCotizacionCompleta, guardarCotizacion } from '../services/cotizacionesService';
import { calcularTotales, ESTADOS_COTIZACION } from '../utils/cotizaciones';
import { formatoMoneda } from '../utils/servicios';

const P = 'var(--color-primario,#DB2777)';
const label = { display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: '#374151' };
const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const seccion = { marginBottom: '18px', paddingBottom: '18px', borderBottom: '1px solid #F3F4F6' };

let contadorKey = 0;
const nuevaLinea = (base) => ({ key: `l${++contadorKey}`, tipo: 'libre', descripcion: '', cantidad: '1', precio_unitario: '0', descuento: '', impuesto_pct: '', servicio_id: null, producto_id: null, ...base });

export default function CotizacionForm({ cotizacion = null, onCerrar, onGuardado }) {
  const editando = !!(cotizacion && cotizacion.id);
  const { negocio } = useAuth();
  const { mostrarToast } = useToast();
  const moneda = negocio?.moneda || 'L';

  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [adicionales, setAdicionales] = useState([]);

  const [clienteId, setClienteId] = useState('');
  const [items, setItems] = useState([]);
  const [descuento, setDescuento] = useState('');
  const [anticipo, setAnticipo] = useState('');
  const [notas, setNotas] = useState('');
  const [terminos, setTerminos] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [estado, setEstado] = useState('borrador');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    Promise.all([
      obtenerClientes().catch(() => []),
      obtenerServicios().catch(() => []),
      obtenerProductos().catch(() => []),
      obtenerAdicionalesPredef().catch(() => []),
    ]).then(([cl, sv, pr, ad]) => {
      if (!vivo) return;
      setClientes(cl); setServicios(sv.filter((s) => s.activo !== false)); setProductos(pr); setAdicionales(ad);
    });
    return () => { vivo = false; };
  }, []);

  // Al editar: precargar la cotización.
  useEffect(() => {
    if (!editando) { setCargando(false); return; }
    let vivo = true;
    obtenerCotizacionCompleta(cotizacion.id).then((c) => {
      if (!vivo || !c) { setCargando(false); return; }
      setClienteId(c.cliente_id || '');
      setDescuento(c.descuento ? String(c.descuento) : '');
      setAnticipo(c.anticipo ? String(c.anticipo) : '');
      setNotas(c.notas || ''); setTerminos(c.terminos || '');
      setFechaVencimiento(c.fecha_vencimiento || '');
      setEstado(c.estado || 'borrador');
      setItems((c.items || []).map((it) => nuevaLinea({
        tipo: it.tipo, servicio_id: it.servicio_id, producto_id: it.producto_id,
        descripcion: it.descripcion, cantidad: String(it.cantidad), precio_unitario: String(it.precio_unitario),
        descuento: it.descuento ? String(it.descuento) : '', impuesto_pct: it.impuesto_pct ? String(it.impuesto_pct) : '',
      })));
      setCargando(false);
    }).catch(() => setCargando(false));
    return () => { vivo = false; };
  }, [editando, cotizacion]);

  const totales = useMemo(() => calcularTotales(
    items.map((it) => ({ cantidad: it.cantidad, precio_unitario: it.precio_unitario, descuento: it.descuento, impuesto_pct: it.impuesto_pct })),
    descuento, anticipo,
  ), [items, descuento, anticipo]);

  const setLinea = (key, campo, valor) => setItems((prev) => prev.map((l) => l.key === key ? { ...l, [campo]: valor } : l));
  const quitarLinea = (key) => setItems((prev) => prev.filter((l) => l.key !== key));

  const agregarServicio = (id) => {
    const s = servicios.find((x) => x.id === id);
    if (!s) return;
    setItems((prev) => [...prev, nuevaLinea({
      tipo: 'servicio', servicio_id: s.id, descripcion: s.nombre,
      precio_unitario: String(s.precio || 0), impuesto_pct: s.impuesto_pct ? String(s.impuesto_pct) : '',
    })]);
  };
  const agregarProducto = (id) => {
    const p = productos.find((x) => x.id === id);
    if (!p) return;
    setItems((prev) => [...prev, nuevaLinea({
      tipo: 'producto', producto_id: p.id,
      descripcion: p.codigo ? `${p.nombre} (${p.codigo})` : p.nombre,
      precio_unitario: String(p.precio_venta || 0),
    })]);
  };
  const agregarAdicional = (a) => setItems((prev) => [...prev, nuevaLinea({
    tipo: 'adicional', descripcion: a.nombre, precio_unitario: String(a.precio || 0),
    impuesto_pct: a.impuesto_pct ? String(a.impuesto_pct) : '',
  })]);

  const guardar = async () => {
    if (items.length === 0) { mostrarToast('Agrega al menos un servicio o producto', 'error'); return; }
    if (items.some((l) => !String(l.descripcion || '').trim())) { mostrarToast('Cada renglón necesita una descripción', 'error'); return; }
    setGuardando(true);
    try {
      const payload = {
        id: editando ? cotizacion.id : null,
        clienteId: clienteId || null,
        items: items.map((l) => ({
          tipo: l.tipo, servicio_id: l.servicio_id || null, producto_id: l.producto_id || null,
          descripcion: l.descripcion, cantidad: Number(l.cantidad) || 0,
          precio_unitario: Number(l.precio_unitario) || 0, descuento: Number(l.descuento) || 0,
          impuesto_pct: Number(l.impuesto_pct) || 0,
        })),
        descuento: Number(descuento) || 0, anticipo: Number(anticipo) || 0,
        notas: notas.trim() || null, terminos: terminos.trim() || null,
        fechaVencimiento: fechaVencimiento || null, estado,
      };
      await guardarCotizacion(payload);
      mostrarToast(editando ? 'Cotización actualizada ✅' : 'Cotización creada ✅', 'exito');
      onGuardado && onGuardado();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally { setGuardando(false); }
  };

  const fmt = (n) => formatoMoneda(n, moneda);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '14px', overflowY: 'auto' }} onClick={guardando ? undefined : onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '720px', maxWidth: '100%', margin: '10px 0', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <h2 style={{ margin: 0, color: P, fontSize: '20px' }}>{editando ? '✏️ Editar cotización' : '🧾 Nueva cotización'}</h2>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        {cargando ? <p style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>⏳ Cargando…</p> : (
        <>
          {/* Cliente + estado */}
          <div style={seccion}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={label}>Cliente</label>
                <select value={clienteId} disabled={guardando} onChange={(e) => setClienteId(e.target.value)} style={input}>
                  <option value="">Sin cliente / general</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Estado</label>
                <select value={estado} disabled={guardando} onChange={(e) => setEstado(e.target.value)} style={input}>
                  {ESTADOS_COTIZACION.filter((e) => e.clave !== 'convertida').map((e) => <option key={e.clave} value={e.clave}>{e.nombre}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Agregar renglones */}
          <div style={seccion}>
            <label style={label}>Agregar al presupuesto</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
              <select value="" disabled={guardando} onChange={(e) => { agregarServicio(e.target.value); e.target.value = ''; }} style={input}>
                <option value="">🧰 + Agregar servicio…</option>
                {servicios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
              <select value="" disabled={guardando} onChange={(e) => { agregarProducto(e.target.value); e.target.value = ''; }} style={input}>
                <option value="">📦 + Agregar producto…</option>
                {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.codigo ? ` (${p.codigo})` : ''}</option>)}
              </select>
            </div>
            {adicionales.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {adicionales.map((a) => (
                  <button key={a.id} type="button" onClick={() => agregarAdicional(a)}
                    style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#78350F', borderRadius: '999px', padding: '4px 11px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    + {a.nombre} · {fmt(a.precio)}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={() => setItems((prev) => [...prev, nuevaLinea({})])} disabled={guardando}
              style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${P}`, background: '#fff', color: P, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>+ Línea libre</button>
          </div>

          {/* Renglones */}
          {items.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '16px', fontSize: '14px' }}>Agrega servicios, productos o adicionales arriba 👆</p>
          ) : (
            <div style={{ marginBottom: '18px' }}>
              {items.map((l, i) => {
                const tot = totales.lineas[i] || { total: 0 };
                const icono = l.tipo === 'servicio' ? '🧰' : l.tipo === 'producto' ? '📦' : l.tipo === 'adicional' ? '➕' : '✏️';
                return (
                  <div key={l.key} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
                      <span title={l.tipo}>{icono}</span>
                      <input value={l.descripcion} disabled={guardando} maxLength={160} placeholder="Descripción del renglón"
                        onChange={(e) => setLinea(l.key, 'descripcion', e.target.value)} style={{ ...input, flex: 1 }} />
                      <button type="button" onClick={() => quitarLinea(l.key)}
                        style={{ padding: '0 12px', borderRadius: '8px', border: '1px solid #FCA5A5', background: '#fff', color: '#DC2626', cursor: 'pointer' }}>🗑️</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr)) auto', gap: '6px', alignItems: 'end' }}>
                      <CampoNumero etiqueta="Cant." valor={l.cantidad} min={0} deshabilitado={guardando} onChange={(v) => setLinea(l.key, 'cantidad', v)} />
                      <CampoNumero etiqueta="Precio" valor={l.precio_unitario} prefijo={moneda} min={0} deshabilitado={guardando} onChange={(v) => setLinea(l.key, 'precio_unitario', v)} />
                      <CampoNumero etiqueta="Desc." valor={l.descuento} prefijo={moneda} min={0} deshabilitado={guardando} onChange={(v) => setLinea(l.key, 'descuento', v)} placeholder="0" />
                      <CampoNumero etiqueta="Imp.%" valor={l.impuesto_pct} sufijo="%" min={0} max={100} deshabilitado={guardando} onChange={(v) => setLinea(l.key, 'impuesto_pct', v)} placeholder="0" />
                      <div style={{ textAlign: 'right', minWidth: '90px', paddingBottom: '10px' }}>
                        <span style={{ fontSize: '11px', color: '#9CA3AF', display: 'block' }}>Total</span>
                        <strong style={{ color: '#111827', fontSize: '14px' }}>{fmt(tot.total)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Descuento global / anticipo / vencimiento */}
          <div style={seccion}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
              <CampoNumero etiqueta="Descuento adicional" valor={descuento} prefijo={moneda} min={0} deshabilitado={guardando} onChange={setDescuento} placeholder="0" />
              <CampoNumero etiqueta="Anticipo recibido" valor={anticipo} prefijo={moneda} min={0} deshabilitado={guardando} onChange={setAnticipo} placeholder="0" />
              <div>
                <label style={label}>Vence el</label>
                <input type="date" value={fechaVencimiento} disabled={guardando} onChange={(e) => setFechaVencimiento(e.target.value)} style={input} />
              </div>
            </div>
          </div>

          {/* Totales */}
          <div style={{ background: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: '12px', padding: '14px', marginBottom: '18px' }}>
            <Fila etiqueta="Subtotal" valor={fmt(totales.subtotal)} />
            {totales.impuesto > 0 && <Fila etiqueta="Impuesto" valor={fmt(totales.impuesto)} />}
            {totales.descuento > 0 && <Fila etiqueta="Descuento" valor={`- ${fmt(totales.descuento)}`} />}
            <Fila etiqueta="Total" valor={fmt(totales.total)} destacado />
            {totales.anticipo > 0 && <>
              <Fila etiqueta="Anticipo" valor={`- ${fmt(totales.anticipo)}`} />
              <Fila etiqueta="Saldo pendiente" valor={fmt(totales.saldo)} destacado />
            </>}
          </div>

          {/* Notas y términos */}
          <div style={seccion}>
            <label style={label}>Información adicional o instrucciones para el cliente (opcional)</label>
            <textarea value={notas} disabled={guardando} rows={3} maxLength={800} onChange={(e) => setNotas(e.target.value)} style={{ ...input, fontFamily: 'inherit', resize: 'vertical', marginBottom: '12px' }} placeholder="Ej. Incluye traslados. Para reservar, envía tu anticipo al número 9999-9999. Precios sujetos a disponibilidad." />
            <label style={label}>Términos y condiciones (opcional)</label>
            <textarea value={terminos} disabled={guardando} rows={2} maxLength={1200} onChange={(e) => setTerminos(e.target.value)} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} placeholder="Ej. Anticipo del 50%. Vigencia 15 días." />
          </div>

          {guardando && (
            <div style={{ marginBottom: '12px', padding: '10px', background: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400E', fontWeight: 600 }}>⏳ Guardando cotización…</p>
            </div>
          )}
          <button onClick={guardar} disabled={guardando}
            style={{ width: '100%', padding: '14px', background: guardando ? '#9CA3AF' : P, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
            {guardando ? '⏳ Guardando…' : (editando ? 'Guardar cambios' : 'Guardar cotización')}
          </button>
        </>
        )}
      </div>
    </div>
  );
}

function Fila({ etiqueta, valor, destacado }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: destacado ? '6px 0 0' : '3px 0', marginTop: destacado ? '4px' : 0, borderTop: destacado ? '1px solid #E9D5FF' : 'none' }}>
      <span style={{ fontSize: destacado ? '15px' : '13px', fontWeight: destacado ? 700 : 500, color: destacado ? '#111827' : '#6B7280' }}>{etiqueta}</span>
      <span style={{ fontSize: destacado ? '16px' : '13px', fontWeight: destacado ? 800 : 600, color: destacado ? P : '#374151' }}>{valor}</span>
    </div>
  );
}
